import { Image } from '@chakra-ui/react';
import {
  Heading,
  Button,
  Flex,
  Box,
  ButtonProps,
  Link,
} from '@everlywell/ui-kit';
import RichTextWrapper from 'components/RichTextWrapper';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { STORE_ROOT } from 'utils/constants/urls';
import { isFullUrl } from 'utils/helpers';
import { replaceRedirectionLinkRoot } from 'utils/helpers/replaceRedirectionLinkRoot';
import { ImageType } from 'utils/types';
import { ContentfulRichText } from 'utils/types/contentfulTypes';

export type WelcomePageProps = {
  program?: string;
  desktopImage?: ImageType | null;
  mobileImage?: ImageType | null;
  heading?: string | null;
  description?: ContentfulRichText | null;
  cta?: Queries.ContentfulCallToAction001 | null;
};

export default function WelcomeToMembership({
  program,
  heading,
  description,
  cta,
  mobileImage,
  desktopImage,
}: WelcomePageProps) {
  const handleButtonClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.MEMBERSHIP.WELCOME_PAGE.CONTINUE_CTA,
        program,
      },
    });
  };

  const hasFullUrl = cta?.url ? isFullUrl(cta?.url) : false;

  const linkTo = hasFullUrl
    ? replaceRedirectionLinkRoot(cta?.url || '')
    : cta?.url;

  const buttonProps: ButtonProps = {
    ...(hasFullUrl
      ? { href: `${STORE_ROOT}/login?my_results_redirect=${linkTo}`, as: 'a' }
      : { to: linkTo, as: Link }),
  };

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      rowGap={6}
      height="100vh"
      padding={[4, 8]}
      sx={{
        opacity: 0,
        animation: 'fadeIn 0.5s ease-in-out forwards',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Flex width="40%" justifyContent="center">
        {mobileImage ? (
          <Image
            display={[null, null, 'none']}
            as={GatsbyImage}
            image={mobileImage?.gatsbyImageData}
            alt={mobileImage?.description || ''}
          />
        ) : null}

        {desktopImage ? (
          <Image
            display={['none', null, 'block']}
            as={GatsbyImage}
            image={desktopImage?.gatsbyImageData}
            alt={desktopImage?.description || ''}
          />
        ) : null}
      </Flex>

      {heading ? <Heading>{heading}</Heading> : null}

      {description ? (
        <Box>
          <RichTextWrapper contentfulRichText={description}></RichTextWrapper>
        </Box>
      ) : null}

      {cta && cta.url ? (
        <Button type="button" onClick={handleButtonClick} {...buttonProps}>
          {cta.label}
        </Button>
      ) : null}
    </Flex>
  );
}
